import type { HTMLMotionProps } from 'framer-motion';

/**
 * Enter/exit motion shared by the anchored overlays (`Popover`, `Dropdown`, `MultiSelect`, and the
 * listboxes of `Select` and `Autocomplete`).
 *
 * HeroUI's default (`scaleSpringOpacity`) grows the panel from `scale(0.8)` on a 300ms spring,
 * which on a tall listbox travels tens of pixels and reads as heavy. This keeps the same
 * shape — a scale from the anchored corner, which is what makes the panel look like it slides
 * out of its trigger — but starts much closer to full size and settles in half the time.
 *
 * The movement is deliberately produced by the scale rather than a `y` offset: HeroUI sets
 * `transform-origin` from the resolved placement, so the panel always grows away from its
 * trigger. A fixed translate would slide the wrong way for `top` and `left` placements.
 *
 * Use framer's `scale` key, never a `transform: 'scale(…)'` string: framer only strips its own
 * known transform keys under `prefers-reduced-motion`, so a raw transform string animates even for
 * users who asked for no motion (and keeps overlays moving under test — see `Provider`).
 */
export const OVERLAY_MOTION_PROPS: Omit<HTMLMotionProps<'div'>, 'ref'> = {
  variants: {
    initial: { opacity: 0, scale: 0.98 },
    enter: { opacity: 1, scale: 1, transition: { duration: 0.2, ease: 'anticipate' } },
    exit: { opacity: 0, scale: 0.98, transition: { duration: 0.2, ease: 'backOut' } },
  },
};
